import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useBackgroundJobsContext } from '../../hooks/useBackgroundJobs';
import { BackgroundJob } from '../../types';
import { JobTimedProgress } from './JobTimedProgress';

function getJobDescription(job: BackgroundJob, t: (key: string) => string): string {
  const baseDescription = (() => {
    switch (job.jobType) {
      case 'generate_cover_image':
        return t('backgroundJobs.generatingCoverImage');
      case 'generate_story_themes':
        return t('backgroundJobs.generatingStoryThemes');
      case 'generate_book_content':
        return t('backgroundJobs.generatingBookContent');
      case 'write_narrative_block':
        return 'Writing narrative block';
      case 'expand_draft_block':
        return 'Expanding draft content';
      case 'expand_all_draft_blocks':
        return 'Expanding all draft blocks';
      case 'regenerate_draft_block':
        return 'Regenerating draft block';
      default:
        return t('backgroundJobs.processingJob');
    }
  })();

  // Add context from targetInfo for beat-related jobs
  const { targetInfo } = job;
  if (targetInfo?.beatTitle && targetInfo?.beatNumber) {
    return `${baseDescription} - Beat ${targetInfo.beatNumber}: ${targetInfo.beatTitle}`;
  }

  return baseDescription;
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-500';
    case 'processing':
      return 'bg-blue-500';
    case 'completed':
      return 'bg-green-500';
    case 'failed':
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
}

export default function BackgroundJobsMenu() {
  const { t } = useTranslation();
  const { activeJobs, removeJob } = useBackgroundJobsContext();
  const [isOpen, setIsOpen] = useState(false);
  const [dismissedJobs, setDismissedJobs] = useState<Set<string>>(new Set());
  const menuRef = useRef<HTMLDivElement>(null);

  // Filter jobs that are either active or recently completed/failed and not dismissed
  const visibleJobs = activeJobs.filter((job) => {
    if (dismissedJobs.has(job.id)) return false;
    if (job.status === 'pending' || job.status === 'processing') return true;
    // Show completed/failed jobs for 10 seconds
    const updatedAt = new Date(job.updatedAt);
    const now = new Date();
    return now.getTime() - updatedAt.getTime() < 10000;
  });

  const activeCount = visibleJobs.filter(
    (j) => j.status === 'pending' || j.status === 'processing'
  ).length;

  // Auto-dismiss completed jobs after 10 seconds
  useEffect(() => {
    const completedJobs = activeJobs.filter(
      (job) => (job.status === 'completed' || job.status === 'failed') && !dismissedJobs.has(job.id)
    );

    if (completedJobs.length > 0) {
      const timer = setTimeout(() => {
        setDismissedJobs((prev) => {
          const newSet = new Set(prev);
          completedJobs.forEach((job) => newSet.add(job.id));
          return newSet;
        });
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [activeJobs, dismissedJobs]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDismiss = (jobId: string) => {
    setDismissedJobs((prev) => new Set(prev).add(jobId));
    removeJob(jobId);
  };

  // Don't render anything if no jobs
  if (visibleJobs.length === 0) {
    return null;
  }

  return (
    <div className="relative" ref={menuRef}>
      {/* Badge button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        title={t('backgroundJobs.activeJobsCount', { count: activeCount })}
      >
        {/* Bell icon */}
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
          />
        </svg>

        {/* Badge count */}
        {activeCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
            <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px] font-bold text-white">
              {activeCount > 9 ? '9+' : activeCount}
            </span>
          </span>
        )}
      </button>

      {/* Dropdown menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed sm:absolute right-2 sm:right-0 mt-2 w-[calc(100vw-1rem)] sm:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t('backgroundJobs.title')}
                </h3>
                {activeCount > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('backgroundJobs.safeToClose')}
                  </span>
                )}
              </div>
            </div>

            {/* Job list */}
            <div className="max-h-80 overflow-y-auto">
              {visibleJobs.map((job) => (
                <div
                  key={job.id}
                  className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                >
                  <div className="flex items-start gap-3">
                    {/* Status indicator */}
                    <div className="mt-1.5 flex-shrink-0">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(job.status)}`} />
                    </div>

                    {/* Job info */}
                    <div className="flex-1 min-w-0">
                      {/* Job type with context (beat/chapter/section) */}
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {getJobDescription(job, t)}
                      </p>

                      {/* Book title */}
                      {job.targetInfo?.bookTitle && (
                        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                          {job.targetInfo.bookTitle}
                        </p>
                      )}

                      {/* Progress bar for pending and processing jobs */}
                      {(job.status === 'pending' || job.status === 'processing') &&
                        (job.estimatedDurationMs && job.status === 'processing' ? (
                          <div className="mt-2">
                            <JobTimedProgress job={job} fallbackMs={20000} />
                          </div>
                        ) : (
                          <div className="mt-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {job.status === 'pending' ? 'Pending' : 'Processing'}
                              </span>
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {job.progress}%
                              </span>
                            </div>
                            <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                              <motion.div
                                className={`h-full ${job.status === 'pending' ? 'bg-yellow-500' : 'bg-blue-500'}`}
                                initial={{ width: 0 }}
                                animate={{ width: `${job.progress}%` }}
                                transition={{ duration: 0.3 }}
                              />
                            </div>
                          </div>
                        ))}

                      {/* Error message for failed jobs */}
                      {job.status === 'failed' && job.errorMessage && (
                        <p className="mt-1 text-xs text-red-500 line-clamp-2">{job.errorMessage}</p>
                      )}
                    </div>

                    {/* Dismiss button for completed/failed */}
                    {(job.status === 'completed' || job.status === 'failed') && (
                      <button
                        onClick={() => handleDismiss(job.id)}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
